/**
 * The upload-kind vocabulary of the upload toast, in one place.
 *
 * Three separate things used to be decided ad hoc inside `UploadToast.tsx`, and
 * a wrong answer in any of them showed up in the user's face:
 *
 *  1. WHICH KIND an upload is — derived from its `section`, the compressor's own
 *     vocabulary (`MediaSection`, validated server-side by
 *     `oneof=hallAd radioAd tvAd orgAvatar collaboratorAvatar`).
 *  2. WHAT IT IS CALLED when nothing supplies a name.
 *  3. WHAT IT IS LABELLED in its chip.
 *
 * Measured bug (QA round 2): `orgAvatar` was mapped to the kind `hall` and
 * `collaboratorAvatar` to `radio`. Neither avatar upload ever receives a name —
 * `receiveNewUploadItem` is only emitted by enterprise-ads-api for ads, so an
 * avatar reaches the toast solely through `publishLocalUploadProgress(id, pct,
 * section)`. The name therefore fell through to the old template
 * `Upload ${feature}` and an organisation logo was listed as «Upload hall» with
 * a chip reading "Hall". Two words wrong at once: it was not a hall upload, and
 * "hall" is the INTERNAL name of the surface the product calls Átrio.
 *
 * `radio | tv | hall` are also wire values — enterprise-ads-api sends them on
 * `receiveNewUploadItem-{orgId}` and returns them from `GET /compressions` — so
 * those three spellings are a contract and must not be renamed. The two avatar
 * kinds are frontend-only; they never cross a service boundary.
 */

import type { MediaSection } from "../../utils/utils";
import type { RolePermissions } from "../PermissionsContext";

export type UploadKind = "radio" | "tv" | "hall" | "orgAvatar" | "collaboratorAvatar";

/** Compressor `section` → upload kind. Total over `MediaSection`. */
export const UPLOAD_SECTION_TO_KIND: Record<MediaSection, UploadKind> = {
  hallAd: "hall",
  radioAd: "radio",
  tvAd: "tv",
  orgAvatar: "orgAvatar",
  collaboratorAvatar: "collaboratorAvatar",
};

/**
 * Chip text. `hall` reads "Átrio" because that is the product's name for the
 * surface; "hall" only ever appears in code, paths and event names.
 */
export const UPLOAD_KIND_CHIP: Record<UploadKind, string> = {
  radio: "Rádio",
  tv: "TV",
  hall: "Átrio",
  orgAvatar: "Organização",
  collaboratorAvatar: "Perfil",
};

/** Name used when the upload carries none of its own. */
export const UPLOAD_KIND_DEFAULT_NAME: Record<UploadKind, string> = {
  radio: "Anúncio de rádio",
  tv: "Anúncio de TV",
  hall: "Anúncio do Átrio",
  orgAvatar: "Logo da organização",
  collaboratorAvatar: "Foto de perfil",
};

/**
 * Which permission decides whether an upload of this kind is listed at all.
 *
 * The progress channel is org-wide (`updateUploadItemProgress-{orgId}`), so this
 * is what keeps one collaborator's toast from showing surfaces they cannot see.
 *
 * `null` means no permission owns the kind: a collaborator's own profile photo
 * is uploaded from `/configuracoes`, which every role reaches, and
 * `RolePermissions` has no "view avatar upload" resource to consult. Routing it
 * through an ads permission — which is what the old `collaboratorAvatar → radio`
 * mapping did — hid a Gestor de Cobrança's own upload entirely (`roomRadioAds`
 * is empty for that template).
 */
export const UPLOAD_KIND_PERMISSION: Record<UploadKind, keyof RolePermissions | null> = {
  radio: "roomRadioAds",
  tv: "roomTvAds",
  hall: "hallAds",
  orgAvatar: "orgInfos",
  collaboratorAvatar: null,
};

/**
 * Shown instead of an empty chip when a kind is not in the table.
 *
 * Only reachable from the wire, where `feature` is handed straight through by
 * `receiveNewUploadItem` and `GET /compressions`. An unmapped kind used to
 * render nothing at all, which reads as a rendering glitch rather than as an
 * unknown kind.
 */
export const UPLOAD_KIND_UNKNOWN_LABEL = "Mídia";

export function uploadKindChip(kind: UploadKind): string {
  return UPLOAD_KIND_CHIP[kind] ?? UPLOAD_KIND_UNKNOWN_LABEL;
}

export function uploadKindDefaultName(kind: UploadKind): string {
  return UPLOAD_KIND_DEFAULT_NAME[kind] ?? UPLOAD_KIND_UNKNOWN_LABEL;
}
